import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Disc3, Sparkles, Headphones } from "lucide-react";

/**
 * Editorial Banner Sections — alternating left/right layout.
 * Showcases curated stories with a strong image + headline + CTA on the opposite side.
 * Admin can later override these via site_content.editorial_blocks (read from props).
 */
export default function EditorialBanners({ items, t, lang }) {
  const pick = (v) => {
    if (v == null) return "";
    if (typeof v === "string") return v;
    return v[lang] || v.en || v.az || Object.values(v)[0] || "";
  };

  const blocks = Array.isArray(items) ? items : [];

  // If the admin has not added any editorial blocks, render nothing —
  // the section disappears entirely from the homepage.
  if (blocks.length === 0) return null;

  const Icon = ({ name, size = 18 }) => {
    if (name === "vinyl") return <Disc3 size={size} className="vinyl-spin" />;
    if (name === "sparkles") return <Sparkles size={size} />;
    if (name === "headphones") return <Headphones size={size} />;
    return null;
  };

  return (
    <section className="border-t border-[var(--border-soft)] bg-[var(--bg)]" data-testid="editorial-banners">
      <div className="max-w-screen-2xl mx-auto px-5 md:px-12 py-20 md:py-28">
        {(t?.sections?.editorialSub || t?.sections?.editorial) && (
          <div className="flex items-end justify-between gap-6 mb-14 md:mb-20 flex-wrap">
            <div>
              {t?.sections?.editorialSub && (
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-px bg-[var(--accent)]"/>
                  <span className="section-eyebrow">{t.sections.editorialSub}</span>
                </div>
              )}
              {t?.sections?.editorial && (
                <h2 className="section-title text-4xl md:text-5xl lg:text-[56px] leading-[1.04]">
                  {t.sections.editorial}
                </h2>
              )}
            </div>
          </div>
        )}

        <div className="space-y-20 md:space-y-28">
          {blocks.map((b, idx) => {
            const isRight = b.align === "right";
            return (
              <div
                key={b.id || idx}
                className={`grid md:grid-cols-12 gap-6 md:gap-12 items-center ${isRight ? "md:[direction:rtl]" : ""}`}
                data-testid={`editorial-block-${idx}`}
              >
                {/* Image side */}
                <div className="md:col-span-7 md:[direction:ltr]">
                  <div className="relative aspect-[16/10] md:aspect-[5/4] overflow-hidden group bg-[var(--surface)]">
                    <img
                      src={b.image_url}
                      alt={pick(b.title)}
                      className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-[1400ms] ease-out"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/5 to-transparent" />
                    {/* Floating stat badge — dark glass with white text */}
                    {b.stat_value && (
                      <div className={`absolute bottom-5 ${isRight ? "right-5" : "left-5"} bg-black/55 backdrop-blur-md border border-white/15 px-4 py-3 rounded-sm`}>
                        <div className="flex items-center gap-2.5">
                          <div className="w-7 h-7 rounded-full bg-white/15 border border-white/25 text-white flex items-center justify-center">
                            <Icon name={b.icon} size={13} />
                          </div>
                          <div>
                            <div className="font-display text-lg leading-none text-white font-medium">{b.stat_value}</div>
                            <div className="metadata text-[10px] mt-1 text-white/70 normal-case tracking-wider">{pick(b.stat_label)}</div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Text side */}
                <div className="md:col-span-5 md:[direction:ltr]">
                  <div className={`max-w-lg ${isRight ? "md:ml-auto md:text-right" : ""}`}>
                    {pick(b.eyebrow) && (
                      <div className={`flex items-center gap-3 mb-5 ${isRight ? "md:justify-end" : ""}`}>
                        {!isRight && <div className="w-8 h-px bg-[var(--accent)]"/>}
                        <span className="metadata text-[var(--accent)]">{pick(b.eyebrow)}</span>
                        {isRight && <div className="w-8 h-px bg-[var(--accent)]"/>}
                      </div>
                    )}
                    <h3 className="section-title text-3xl md:text-4xl lg:text-[44px] leading-[1.05] whitespace-pre-line text-[var(--text)]">
                      {pick(b.title)}
                    </h3>
                    {pick(b.desc) && (
                      <p className="mt-5 text-[14.5px] md:text-[15px] leading-[1.7] text-[var(--text-2)] font-light">
                        {pick(b.desc)}
                      </p>
                    )}
                    <div className={`mt-8 flex ${isRight ? "md:justify-end" : ""}`}>
                      <Link to={b.cta_url || "/catalog"} className="btn-primary" data-testid={`editorial-cta-${idx}`}>
                        {pick(b.cta_label) || "DETALLAR"} <ArrowRight size={14} />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
